import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SmartGoal, HabitTask, Challenge90, WheelAssessment, HydrationLog, UserBadge, ChallengeJournalEntry, MiniGoal, WeeklyPillarAssessment, PillarSlug } from '@/types';
import { calculateStreak } from '@/lib/utils';
import { habitsService } from '@/services/habitsService';
import { goalsService } from '@/services/goalsService';
import { performanceService } from '@/services/performanceService';
import { useAuthStore } from '@/stores/useAuthStore';
import { toast } from 'sonner';

interface AppState {
  goals: SmartGoal[];
  habits: HabitTask[];
  challenges: Challenge90[]; // FASE 3: Mudança de challenge singular para challenges array
  wheelAssessments: WheelAssessment[];
  weeklyAssessments: WeeklyPillarAssessment[];
  hydrationLogs: HydrationLog[];
  userBadges: UserBadge[];
  
  // Goals
  addGoal: (goal: SmartGoal) => void;
  updateGoal: (id: string, data: Partial<SmartGoal>) => void;
  deleteGoal: (id: string) => void;
  
  // Habits
  addHabit: (habit: HabitTask) => void;
  updateHabit: (id: string, data: Partial<HabitTask>) => void;
  deleteHabit: (id: string) => void;
  toggleHabitCheck: (id: string, date: string) => Promise<void>;
  
  // Challenge (mantém métodos para compatibilidade)
  startChallenge: (challenge: Challenge90) => void;
  updateChallenge: (challengeId: string, data: Partial<Challenge90>) => void;
  archiveChallenge: (challengeId: string) => void;
  checkChallengeDay: (challengeId: string, date: string) => void;
  addJournalEntry: (challengeId: string, date: string, entry: Omit<ChallengeJournalEntry, 'date' | 'createdAt'>) => void;
  updateJournalEntry: (challengeId: string, date: string, updates: Partial<ChallengeJournalEntry>) => void;
  
  // Getter para compatibilidade (retorna primeiro desafio ativo)
  get challenge(): Challenge90 | null;
  
  // Mini Goals
  addMiniGoal: (goalId: string, miniGoal: Omit<MiniGoal, 'id' | 'parentGoalId'>) => void;
  updateMiniGoal: (goalId: string, miniGoalId: string, updates: Partial<MiniGoal>) => void;
  toggleMiniGoal: (goalId: string, miniGoalId: string) => Promise<void>;
  deleteMiniGoal: (goalId: string, miniGoalId: string) => void;
  
  // Wheel
  saveWheelAssessment: (assessment: WheelAssessment) => void;
  
  // Weekly Assessments
  calculateWeeklyScores: (userId: string, pillars: PillarSlug[]) => void;
  
  // Hydration
  logHydration: (userId: string, ml: number) => void;
  
  // Badges
  awardBadge: (userId: string, badgeId: string) => void;
  
  // Data setters (para sincronização)
  setGoals: (goals: SmartGoal[]) => void;
  setHabits: (habits: HabitTask[]) => void;
  setChallenges: (challenges: Challenge90[]) => void;
  setChallenge: (challenge: Challenge90 | null) => void; // Compatibilidade
}

// ✅ FASE 2: REMOVER PERSIST - Dados devem vir APENAS do Supabase
// Removendo persist para evitar conflitos entre localStorage e banco de dados
export const useAppStore = create<AppState>()((set, get) => ({
      goals: [],
      habits: [],
      challenges: [], // FASE 3: Array vazio inicial
      wheelAssessments: [],
      weeklyAssessments: [],
      hydrationLogs: [],
      userBadges: [],
      
      // Getter para compatibilidade - retorna primeiro desafio ativo não arquivado
      get challenge() {
        return get().challenges.find(c => !c.archived) || null;
      },

      // Goals
      addGoal: (goal) => set((state) => ({ goals: [...state.goals, goal] })),
      
      updateGoal: (id, data) =>
        set((state) => ({
          goals: state.goals.map((g) => (g.id === id ? { ...g, ...data } : g)),
        })),
      
      deleteGoal: (id) =>
        set((state) => ({ goals: state.goals.filter((g) => g.id !== id) })),

      // Habits
      addHabit: (habit) => set((state) => ({ habits: [...state.habits, habit] })),
      
      updateHabit: (id, data) =>
        set((state) => ({
          habits: state.habits.map((h) => (h.id === id ? { ...h, ...data } : h)),
        })),
      
      deleteHabit: (id) =>
        set((state) => ({ habits: state.habits.filter((h) => h.id !== id) })),
      
  toggleHabitCheck: async (id, date) => {
    try {
      // Persistir no banco PRIMEIRO
      await habitsService.toggleHabitDate(id, date);
      
      // Depois atualizar estado local
      set((state) => ({
        habits: state.habits.map((h) => {
          if (h.id === id) {
            const history = h.history ?? [];
            const isCompleted = history.includes(date);
            const newHistory = isCompleted
              ? history.filter((d) => d !== date)
              : [...history, date];
            return { ...h, history: newHistory };
          }
          return h;
        }),
      }));

      // ✅ Recalcular pontuações semanais imediatamente após check-in
      const { user } = useAuthStore.getState();
      const { habits } = get();
      if (user?.id && user.pilares_foco) {
        await performanceService.calculateAndSaveWeeklyScores(
          user.id,
          user.pilares_foco,
          habits
        );
      }
    } catch (error) {
      console.error('❌ Erro ao marcar hábito:', error);
      toast.error('Não foi possível salvar o check-in. Tente novamente.');
      throw error;
    }
  },

      // Challenge - FASE 3: Atualizado para trabalhar com array
      startChallenge: (challenge) => 
        set((state) => ({
          challenges: [...state.challenges, challenge],
        })),
      
      updateChallenge: (challengeId, data) =>
        set((state) => ({
          challenges: state.challenges.map((c) => 
            c.id === challengeId ? { ...c, ...data } : c
          ),
        })),
      
      archiveChallenge: (challengeId) =>
        set((state) => ({
          challenges: state.challenges.map((c) =>
            c.id === challengeId ? { ...c, archived: true, status: 'abandonado' as const } : c
          ),
        })),
      
      checkChallengeDay: (challengeId, date) =>
        set((state) => {
          return {
            challenges: state.challenges.map((c) => {
              if (c.id !== challengeId) return c;
              
              const completedDates = c.completedDates.includes(date)
                ? c.completedDates
                : [...c.completedDates, date];
              
              const sortedDates = completedDates.sort();
              const streak = calculateStreak(sortedDates);
              const bestStreak = Math.max(streak, c.bestStreak || 0);
              const totalDays = completedDates.length;
              
              return {
                ...c,
                completedDates,
                streak,
                bestStreak,
                totalDays,
                status: totalDays >= 90 ? 'concluido' as const : 'ativo' as const,
              };
            }),
          };
        }),

      addJournalEntry: (challengeId, date, entry) =>
        set((state) => {
          return {
            challenges: state.challenges.map((c) => {
              if (c.id !== challengeId) return c;
              
              const newEntry: ChallengeJournalEntry = {
                ...entry,
                date,
                createdAt: new Date().toISOString(),
              };
              
              const existingIndex = c.journalEntries.findIndex(e => e.date === date);
              const journalEntries = existingIndex >= 0
                ? c.journalEntries.map((e, i) => i === existingIndex ? newEntry : e)
                : [...c.journalEntries, newEntry];
              
              return {
                ...c,
                journalEntries,
              };
            }),
          };
        }),

      updateJournalEntry: (challengeId, date, updates) =>
        set((state) => {
          return {
            challenges: state.challenges.map((c) => {
              if (c.id !== challengeId) return c;
              
              return {
                ...c,
                journalEntries: c.journalEntries.map(entry =>
                  entry.date === date
                    ? { ...entry, ...updates, updatedAt: new Date().toISOString() }
                    : entry
                ),
              };
            }),
          };
        }),

      // Mini Goals
      addMiniGoal: (goalId, miniGoal) =>
        set((state) => ({
          goals: state.goals.map(g =>
            g.id === goalId
              ? {
                  ...g,
                  miniGoals: [
                    ...g.miniGoals,
                    { ...miniGoal, id: crypto.randomUUID(), parentGoalId: goalId },
                  ],
                }
              : g
          ),
        })),

      updateMiniGoal: (goalId, miniGoalId, updates) =>
        set((state) => ({
          goals: state.goals.map(g =>
            g.id === goalId
              ? {
                  ...g,
                  miniGoals: g.miniGoals.map(mg =>
                    mg.id === miniGoalId ? { ...mg, ...updates } : mg
                  ),
                }
              : g
          ),
        })),

  toggleMiniGoal: async (goalId, miniGoalId) => {
    try {
      // 1. Buscar a mini-meta atual
      const goal = get().goals.find(g => g.id === goalId);
      const miniGoal = goal?.miniGoals.find(mg => mg.id === miniGoalId);
      
      if (!miniGoal) {
        toast.error('Mini-meta não encontrada');
        return;
      }

      // 2. Persistir no banco de dados
      await goalsService.toggleMiniGoal(miniGoalId, !miniGoal.completed);

      // 3. Atualizar estado local apenas após sucesso
      set((state) => ({
        goals: state.goals.map(g =>
          g.id === goalId
            ? {
                ...g,
                miniGoals: g.miniGoals.map(mg =>
                  mg.id === miniGoalId
                    ? {
                        ...mg,
                        completed: !mg.completed,
                        completedAt: !mg.completed ? new Date().toISOString() : undefined,
                      }
                    : mg
                ),
              }
            : g
        ),
      }));

      // 4. Feedback para usuário
      toast.success(
        !miniGoal.completed 
          ? 'Mini-meta concluída! 🎉' 
          : 'Mini-meta marcada como pendente'
      );
    } catch (error) {
      console.error('❌ Erro ao atualizar mini-meta:', error);
      toast.error('Erro ao atualizar mini-meta. Tente novamente.');
    }
  },

      deleteMiniGoal: (goalId, miniGoalId) =>
        set((state) => ({
          goals: state.goals.map(g =>
            g.id === goalId
              ? {
                  ...g,
                  miniGoals: g.miniGoals.filter(mg => mg.id !== miniGoalId),
                }
              : g
          ),
        })),

      // Wheel
      saveWheelAssessment: (assessment) =>
        set((state) => ({
          wheelAssessments: [...state.wheelAssessments, assessment],
        })),

      // Weekly Assessments
      calculateWeeklyScores: (userId, pillars) =>
        set((state) => {
          const today = new Date();
          const currentMonday = getMonday(today);
          const weekStartISO = currentMonday.toISOString().split('T')[0];
          const weekEndISO = new Date(currentMonday.getTime() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          
          // Check if already calculated for this week
          const existingAssessment = state.weeklyAssessments.find(
            a => a.userId === userId && a.weekStartISO === weekStartISO
          );
          
          if (existingAssessment) return state;
          
          // Calculate scores based on habit completion rate
          const scores: Partial<Record<PillarSlug, number>> = {};
          const weekDates = getWeekDates(currentMonday);
          
          pillars.forEach(pillar => {
            const pillarHabits = state.habits.filter(h => h.pillar === pillar && h.active);
            if (pillarHabits.length === 0) {
              scores[pillar] = 5; // Default if no habits
              return;
            }
            
            let totalPossible = 0;
            let totalCompleted = 0;
            
            pillarHabits.forEach(habit => {
              weekDates.forEach(date => {
                if (habit.frequency === 'daily' || shouldHabitRunOnDate(habit, date)) {
                  totalPossible++;
                  if (habit.history.includes(date)) {
                    totalCompleted++;
                  }
                }
              });
            });
            
            // Convert completion rate to 0-10 score
            const completionRate = totalPossible > 0 ? totalCompleted / totalPossible : 0.5;
            scores[pillar] = Math.round(completionRate * 10);
          });
          
          const newAssessment: WeeklyPillarAssessment = {
            id: crypto.randomUUID(),
            userId,
            weekStartISO,
            weekEndISO,
            scores,
            createdAt: new Date().toISOString(),
          };
          
          return {
            weeklyAssessments: [...state.weeklyAssessments, newAssessment],
          };
        }),

      // Hydration
      logHydration: (userId, ml) =>
        set((state) => {
          const today = new Date().toISOString().split('T')[0];
          const existingLog = state.hydrationLogs.find(
            (log) => log.userId === userId && log.dateISO === today
          );
          
          if (existingLog) {
            return {
              hydrationLogs: state.hydrationLogs.map((log) =>
                log.userId === userId && log.dateISO === today
                  ? {
                      ...log,
                      mlTotal: log.mlTotal + ml,
                      logs: [...log.logs, { time: new Date().toISOString(), ml }],
                    }
                  : log
              ),
            };
          }
          
          return {
            hydrationLogs: [
              ...state.hydrationLogs,
              {
                userId,
                dateISO: today,
                mlTotal: ml,
                logs: [{ time: new Date().toISOString(), ml }],
              },
            ],
          };
        }),

      // Badges - Agora persiste no banco
      awardBadge: async (userId, badgeId) => {
        try {
          const { badgesService } = await import('@/services/badgesService');
          const success = await badgesService.awardBadge(userId, badgeId);
          if (success) {
            // Atualizar estado local
            set((state) => ({
              userBadges: [
                ...state.userBadges,
                { userId, badgeId, earnedAt: new Date().toISOString() },
              ],
            }));
          }
        } catch (error) {
          console.error('Erro ao conceder badge:', error);
        }
      },
      
      // Data setters (para sincronização)
      setGoals: (goals) => set({ goals }),
      setHabits: (habits) => {
        console.log('📥 setHabits chamado com', habits.length, 'hábitos');
        set({ 
          habits: habits.map(h => ({
            ...h,
            history: h.history ?? [],
            daysOfWeek: h.daysOfWeek ?? [],
            pillars: h.pillars ?? []
          }))
        });
      },
  setChallenges: (challenges) => set({ 
    challenges: challenges.map(c => ({
      ...c,
      completedDates: c.completedDates ?? [],
      journalEntries: c.journalEntries ?? []
    }))
  }),
      setChallenge: (challenge) => set({ 
        challenges: challenge ? [{
          ...challenge,
          completedDates: challenge.completedDates ?? [],
          journalEntries: challenge.journalEntries ?? []
        }] : [] 
      }),
    }));

// Função calculateStreak importada de @/lib/utils

// Helper: get Monday of the week
function getMonday(date: Date): Date {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(date.setDate(diff));
}

// Helper: get all dates in a week
function getWeekDates(monday: Date): string[] {
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
}

// Helper: check if habit should run on a specific date
function shouldHabitRunOnDate(habit: HabitTask, dateISO: string): boolean {
  if (habit.frequency === 'daily') return true;
  
  if (habit.frequency === 'weekly' && habit.daysOfWeek) {
    const date = new Date(dateISO);
    const dayOfWeek = date.getDay();
    return habit.daysOfWeek.includes(dayOfWeek);
  }
  
  if (habit.frequency === 'monthly' && habit.dayOfMonth) {
    const date = new Date(dateISO);
    return date.getDate() === habit.dayOfMonth;
  }
  
  return false;
}
